import { BeamButtonProps, Button, Css, useTestIds } from "@homebound/beam";
import { StepActions } from "src/components/stepper/StepActions";
import { useStepperContext } from "src/components/stepper/StepperContext";

type StepNavProps = {
  /** URL to navigate to when the user cancels out of the flow. */
  cancelUrl: string;
  /** Disables save actions when the form is invalid or read-only. Navigation is always enabled. */
  saveDisabled?: BeamButtonProps["disabled"];
  /** Runs the shared `save()` and owns post-save navigation, called by the final step's "Save" button. */
  onSave: () => Promise<void>;
  /** Label for the final step's primary save button (e.g. "Create" vs "Save Options"). */
  saveLabel?: string;
};

/**
 * Renders the stepper's footer actions into the stepper bar via `StepActions`.
 *
 * - First step (Details): Cancel · Next
 * - Last step (Variants): Cancel · Back · Save
 *
 * Save only ever happens on the last step, since slot changes on the first step (e.g. adding a
 * new slot) can trigger material pre-fill logic that's only visible on the Variants step — saving
 * from the first step risked committing hidden changes the user didn't intend.
 */
export function StepNav(props: StepNavProps) {
  const { cancelUrl, saveDisabled, onSave, saveLabel = "Save Options" } = props;
  const { nextStep, prevStep, isLastStep } = useStepperContext();
  const tid = useTestIds({}, "stepNav");

  return (
    <StepActions>
      <div css={Css.df.aic.gap1.$}>
        <Button label="Cancel" variant="quaternary" onClick={cancelUrl} {...tid.cancel} />
        {isLastStep ? (
          <>
            <Button label="Back" variant="secondary" onClick={prevStep} {...tid.back} />
            <Button label={saveLabel} disabled={saveDisabled} onClick={onSave} {...tid.save} />
          </>
        ) : (
          <Button label="Next" onClick={nextStep} {...tid.next} />
        )}
      </div>
    </StepActions>
  );
}
